
import React from 'react';
import { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import AnimatedCard from '../AnimatedCard';

interface FeatureCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  bulletPoints: string[];
  linkText: string;
  linkUrl: string;
  animation?: 'fade-in' | 'scale-in' | 'slide-in-left' | 'slide-in-right';
  delay?: number;
}

const FeatureCard = ({
  icon: Icon,
  title,
  description,
  bulletPoints,
  linkText,
  linkUrl,
  animation = 'fade-in',
  delay = 0,
}: FeatureCardProps) => {
  return (
    <AnimatedCard animation={animation} delay={delay}>
      <div className="rounded-lg bg-card/30 hover:bg-card/40 transition-colors duration-300 h-full flex flex-col">
        {/* Header with icon and title inline */}
        <div className="p-5 bg-primary/10 rounded-t-lg flex items-center gap-3">
          <div className="bg-primary/20 p-3 rounded-md text-primary-foreground">
            <Icon className="h-6 w-6" />
          </div>
          <h3 className="text-xl font-semibold text-white">{title}</h3>
        </div>
        
        {/* Body */}
        <div className="p-6 flex-grow">
          <p className="text-foreground/80 mb-6">{description}</p>
          
          <ul className="space-y-3 mb-6">
            {bulletPoints.map((point, index) => (
              <li key={index} className="flex items-start">
                <span className="text-primary mr-2 text-lg">•</span>
                <span className="text-foreground/80">{point}</span>
              </li>
            ))}
          </ul>
          
          <Link to={linkUrl} className="text-primary hover:text-primary/80 flex items-center gap-2 transition-colors">
            <span>{linkText}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="ml-1"
            >
              <path d="M7 7h10v10" />
              <path d="M7 17 17 7" />
            </svg>
          </Link>
        </div>
      </div>
    </AnimatedCard>
  );
};

export default FeatureCard;
